import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

/* =============================================================================
   Cursor trail — small firefly-yellow sparks that fade behind the pointer, plus
   a big warm halo "flash" at each click so the butt reads as glowing bright.
   The actual cursor glyph is set via CSS `cursor: url(...)` in App.css. Silently
   disabled on touch / coarse-pointer devices where there is no cursor to follow.
   Also disabled inside the /admin back office so admins get a native workflow.
============================================================================= */
export const CursorSparkle = () => {
  const [trail, setTrail] = useState([]);
  const [flashes, setFlashes] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const location = useLocation();
  const inAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    setEnabled(mq.matches);
    const on = (e) => setEnabled(e.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  useEffect(() => {
    if (!enabled || inAdmin) return;
    let id = 0;
    const onMove = (e) => {
      id += 1;
      setTrail((t) => [...t.slice(-14), { id, x: e.clientX, y: e.clientY }]);
    };
    const onClick = (e) => {
      const fid = Math.random();
      setFlashes((f) => [...f, { id: fid, x: e.clientX, y: e.clientY }]);
      window.setTimeout(() => {
        setFlashes((f) => f.filter((x) => x.id !== fid));
      }, 700);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("click", onClick);
    };
  }, [enabled, inAdmin]);

  if (!enabled || inAdmin) return null;

  return (
    <>
      {trail.map((s, i) => (
        <motion.div
          key={s.id}
          className="firefly-cursor"
          initial={{ opacity: 0.9, scale: 1 }}
          animate={{ opacity: 0, scale: 0.2 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          style={{ left: s.x, top: s.y, width: 6 + (i % 4), height: 6 + (i % 4) }}
        />
      ))}
      <AnimatePresence>
        {flashes.map((f) => (
          <motion.div
            key={f.id}
            className="firefly-flash"
            initial={{ opacity: 1, scale: 0.4 }}
            animate={{ opacity: 0, scale: 6 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
            style={{ left: f.x, top: f.y }}
          />
        ))}
      </AnimatePresence>
    </>
  );
};
